import type { FaqItem } from "../../lib/faqData";

// Accessible accordion FAQ section. Uses native <details>/<summary> so
// keyboard focus + screen reader expansion work without JS, and so the
// section is fully crawlable for SEO (the answer text is in the DOM
// regardless of open state).
//
// Visual register matches the rest of the marketing surfaces:
//   • Cormorant display heading
//   • Sage border + stone or charcoal card surface
//   • Forest-green / gold chevron that flips on open
//
// Used on /pricing (full set), / (trust-only set before FinalCTA), and
// /help (trust-only set under the feature walkthroughs).

interface Props {
  items: FaqItem[];
  // Section heading. Defaults to "Questions before you start" since
  // that's the framing on the homepage (the most-trafficked surface
  // that uses this component).
  heading?: string;
  // Optional one-line intro under the heading. Lets each surface frame
  // the FAQ slightly differently without forking the component.
  intro?: string;
  // Optional anchor id so other components can link to the section
  // (e.g. a "More questions" link from the hero or footer).
  id?: string;
  // Optional eyebrow label above the heading. Used on the homepage to
  // match the visual rhythm of Methodology / WhereWeAreWeak.
  eyebrow?: string;
}

export default function Faq({ items, heading = "Questions before you start", intro, id, eyebrow }: Props) {
  return (
    <section
      id={id}
      className="py-20 sm:py-24 bg-maren-stone dark:bg-maren-night border-t border-maren-sage/30 dark:border-maren-sage/20"
    >
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-10">
          {eyebrow && (
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-maren-green dark:text-maren-gold mb-3">
              {eyebrow}
            </p>
          )}
          <h2 className="text-3xl sm:text-4xl font-display font-medium tracking-tight text-maren-charcoal dark:text-maren-stone leading-tight">
            {heading}
          </h2>
          {intro && (
            <p className="mt-4 text-sm sm:text-base text-maren-charcoal/70 dark:text-maren-stone/75 leading-relaxed max-w-2xl mx-auto">
              {intro}
            </p>
          )}
        </div>

        <div className="divide-y divide-maren-sage/40 dark:divide-maren-sage/20 rounded-2xl border border-maren-sage/40 dark:border-maren-sage/20 bg-white dark:bg-maren-charcoal overflow-hidden shadow-sm">
          {items.map((f) => (
            <details key={f.q} className="group">
              <summary className="flex items-start justify-between gap-4 cursor-pointer px-5 sm:px-6 py-4 text-sm sm:text-[15px] font-semibold text-maren-charcoal dark:text-maren-stone hover:bg-maren-stone/50 dark:hover:bg-maren-charcoal-elevated/60 transition list-none [&::-webkit-details-marker]:hidden">
                <span>{f.q}</span>
                <span
                  className="ml-3 text-maren-charcoal/50 dark:text-maren-stone/55 group-open:rotate-180 transition-transform shrink-0 mt-0.5"
                  aria-hidden="true"
                >
                  ▾
                </span>
              </summary>
              <div className="px-5 sm:px-6 pb-5 -mt-1 text-sm sm:text-[15px] text-maren-charcoal/75 dark:text-maren-stone/80 leading-relaxed">
                {f.a}
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}
